/* eslint-disable react/no-unescaped-entities */
import { FC } from "react";
import { Link } from "react-router-dom";
import { BreadcrumbSchema, Seo } from "@shared/components";

export const PrivacyPage: FC = () => {
  return (
    <>
      <Seo
        title="Политика конфиденциальности | Polotno"
        description="Политика конфиденциальности сервиса Polotno: как мы собираем, используем, храним и защищаем персональные данные пользователей сайта и Telegram-бота."
        path="/privacy"
      />
      <BreadcrumbSchema items={[{ name: "Главная", url: "/" }, { name: "Политика конфиденциальности" }]} />
      <div className="container mx-auto py-12 px-4 md:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-6 text-center">Политика конфиденциальности</h1>
        <p className="text-muted-foreground text-center mb-10">Последнее обновление: январь 2026</p>

        <div className="max-w-3xl mx-auto prose prose-lg dark:prose-invert">
          <p className="mb-6">
            Настоящая Политика применяется ко всей информации, которую мы можем получить о посетителях веб-сайта{" "}
            <a
              href="https://polotno.tech"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              https://polotno.tech
            </a>{" "}
            (далее — «Сайт»), о пользователях Сервиса «Полотно», а также о пользователях Telegram-бота «Полотно»,
            расположенного по адресу{" "}
            <a
              href="https://t.me/PolontoTranscribe_bot"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              https://t.me/PolontoTranscribe_bot
            </a>{" "}
            (далее — «Бот»).
          </p>

          <h2 className="text-2xl font-semibold mb-4">1. Общие положения</h2>
          <p className="mb-6">
            Настоящая политика конфиденциальности (далее — Политика) регулирует обработку персональных данных
            пользователей сервиса «Полотно» (далее — Сервис).
          </p>
          <p className="mb-6">
            Пользователь, посещая наш сайт, соглашается с условиями данной Политики.
          </p>

          <h2 className="text-2xl font-semibold mb-4">2. Собираемые данные</h2>
          <p className="mb-4">Мы собираем следующие данные при использовании Сервиса:</p>
          <ul className="list-disc pl-6 mb-6">
            <li>
              <strong>Контактные данные:</strong> имя, адрес электронной почты, данные профиля Google/Яндекс при
              авторизации через соответствующие сервисы.
            </li>
            <li>
              <strong>Файлы для транскрибации:</strong> загружаемые аудио и видеофайлы.
            </li>
            <li>
              <strong>Метаданные устройств:</strong> IP-адрес, тип браузера, операционная система.
            </li>
          </ul>

          <h2 className="text-2xl font-semibold mb-4">3. Цели обработки данных</h2>
          <p className="mb-4">Мы используем персональные данные для следующих целей:</p>
          <ul className="list-disc pl-6 mb-6">
            <li>Предоставление услуг по транскрибации аудио и видео в текст.</li>
            <li>Поддержка и улучшение качества Сервиса.</li>
            <li>Связь с пользователями для решения вопросов по предоставляемым услугам.</li>
          </ul>

          <h2 className="text-2xl font-semibold mb-4">4. Передача данных третьим лицам</h2>
          <p className="mb-6">
            Мы не передаем ваши персональные данные третьим лицам, за исключением случаев запроса данных от
            государственных органов в рамках закона.
          </p>

          <h2 className="text-2xl font-semibold mb-4">5. Защита данных</h2>
          <p className="mb-4">Мы принимаем меры для обеспечения безопасности ваших данных, включая:</p>
          <ul className="list-disc pl-6 mb-6">
            <li>Использование протоколов шифрования (SSL/TLS) для передачи данных.</li>
            <li>Регулярное обновление программного обеспечения для защиты от уязвимостей.</li>
          </ul>

          <h2 className="text-2xl font-semibold mb-4">6. Хранение данных</h2>
          <p className="mb-6">
            Данные хранятся в течение срока, необходимого для выполнения услуг, но не более 1 года с момента последнего
            взаимодействия пользователя с Сервисом, за исключением случаев, предусмотренных законодательством.
          </p>

          <h2 className="text-2xl font-semibold mb-4">7. Права пользователей</h2>
          <p className="mb-4">Пользователи имеют право:</p>
          <ul className="list-disc pl-6 mb-6">
            <li>Получать информацию о своих персональных данных, обрабатываемых нами.</li>
            <li>Требовать обновления, блокировки или удаления своих данных.</li>
            <li>Отозвать согласие на обработку данных.</li>
            <li>Экспортировать свои данные в машиночитаемом формате.</li>
            <li>Удалить свою учетную запись и все связанные с ней данные.</li>
          </ul>
          <p className="mb-6">
            Для реализации прав обратитесь к нам через{" "}
            <Link
              to="https://t.me/Polotno_support_bot"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              Telegram бот поддержки
            </Link>{" "}
            или по email{" "}
            <a href="mailto:hello@polotno.tech" className="text-blue-500 hover:underline">
              hello@polotno.tech
            </a>
            .
          </p>

          <h2 className="text-2xl font-semibold mb-4">8. Изменения в политике</h2>
          <p className="mb-6">
            Мы оставляем за собой право вносить изменения в Политику. Актуальная версия всегда доступна на странице{" "}
            <a
              href="https://polotno.tech/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              polotno.tech/privacy
            </a>
            .
          </p>
          <p className="mb-6">Рекомендуем регулярно проверять обновления.</p>

          <h2 className="text-2xl font-semibold mb-4">9. Контактная информация</h2>
          <p className="mb-6">
            Если у вас возникли вопросы по политике конфиденциальности, пожалуйста, свяжитесь с нами через{" "}
            <Link
              to="https://t.me/Polotno_support_bot"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              Telegram бот поддержки
            </Link>{" "}
            или по email{" "}
            <a href="mailto:hello@polotno.tech" className="text-blue-500 hover:underline">
              hello@polotno.tech
            </a>
            .
          </p>
        </div>
      </div>
    </>
  );
};
